import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";
import { isEmpty } from "underscore";

import ReusableViewWithMultipleSoruces from "./ReuseViews/ReusableViewsWithMultipleSources";
import { FormatReferenceViewFields } from "./ReuseViews/ReusableViewUtils";
import { useGlobalAppViews } from "./ReuseViews/useGlobalAppViews";
import { ReusableViewIDs } from "../../../types/PageInterfaces";
import { isGlobalTenantAtom } from "../../../atoms/GlobalAtoms";
import { Field, Module } from "../../../types/TenantInterfaces";
import FieldUtils from "../../../utils/FieldUtils";

interface Props {
  globalAppID: string;
  moduleAPIName: string;
  viewID: string | undefined;
  reusableViews: ReusableViewIDs | undefined;
  setReusableViews: Dispatch<SetStateAction<ReusableViewIDs>>;
  module: Module;
}

const empty: [] = [];

export type LinkSettings =
  | {
      Name: string;
      APIName: string;
      Type: "Module" | "Field";
      IsSubitem: false;
      ModuleViews: { ModuleAPIName: string; Views: any[] }[];
    }
  | {
      Name: string;
      APIName: string;
      Type: "Field";
      IsSubitem: true;
      Fields: {
        Name: string;
        APIName: string;
        Type: "Field";
        ModuleViews: { ModuleAPIName: string; Views: any[] }[];
        IsSubitem: false;
      }[];
    };

export default function ReusableViewSettings({
  globalAppID,
  moduleAPIName,
  viewID,
  reusableViews,
  setReusableViews,
  module,
}: Props) {
  const [pickerOpened, setPickerOpened] = useState(false);
  const isEnabled = pickerOpened || !isEmpty(reusableViews);
  const { views, loadingViews, appName } = useGlobalAppViews(
    globalAppID,
    isEnabled,
    "views"
  );
  const viewsLoading = pickerOpened && loadingViews;
  const isGlobalTenant = useAtomValue(isGlobalTenantAtom);

  // An array of all the reference fields in the module.
  const referenceFields = useMemo(() => {
    return FieldUtils.getFieldsBaseOnType(module?.Fields, [
      "SingleReference",
      "MultiReference",
    ]) as Field[];
  }, [moduleAPIName]);

  const finalViews = useMemo(() => {
    const globalAppViews = views || empty;
    const linkSettings: LinkSettings[] = [
      {
        Name: "Record Details",
        APIName: "RecordDetail",
        Type: "Module",
        IsSubitem: false,
        ModuleViews: [
          {
            ModuleAPIName: moduleAPIName,
            Views: globalAppViews.filter(
              (v: any) => v._id !== viewID && v?.ModuleAPIName === moduleAPIName
            ),
          },
        ],
      },
    ];

    const referenceReusableViews = FormatReferenceViewFields({
      referenceFields,
      viewID: viewID ?? "",
      globalAppViews,
    });

    return linkSettings.concat(referenceReusableViews);
  }, [referenceFields, views]);

  const onChange = (
    val: string | null,
    type: "Module" | "Field",
    apiName: string,
    moduleAPIName: string
  ) => {
    if (type === "Module") {
      setReusableViews((prev) => ({
        ...(prev && prev),
        RecordDetail: val ?? "",
      }));
    } else {
      setReusableViews((prev) => ({
        ...(prev && prev),
        ReferenceFields: {
          ...(prev?.ReferenceFields && prev?.ReferenceFields),
          [apiName]: {
            ...(prev?.ReferenceFields?.[apiName] &&
              prev?.ReferenceFields?.[apiName]),
            [moduleAPIName]: val ?? "",
          },
        },
      }));
    }
  };

  return (
    <>
      <h4 className="mb-0">Link Settings</h4>
      <div className="small mb-3">Choose an Existing View to link to:</div>
      {!!finalViews?.length &&
        finalViews.map((fv) => {
          if (fv.Type === "Module") {
            const moduleViews = fv.ModuleViews[0];

            return (
              <div className="tw:pb-2">
                <div className="tw:flex tw:justify-around tw:items-center tw:border tw:border-lighter tw:rounded-sm tw:py-1">
                  <h4 className="tw:m-0">{fv.Name}</h4>
                  <div className="tw:w-xs">
                    <SelectPicker
                      disabled={!isGlobalTenant}
                      block
                      size="sm"
                      preventOverflow
                      placeholder={`Select a View to reuse it.`}
                      searchable={!!moduleViews?.Views?.length}
                      onOpen={() => setPickerOpened(true)}
                      value={reusableViews?.RecordDetail}
                      onChange={(val) => {
                        onChange(
                          val,
                          fv.Type,
                          fv.APIName,
                          moduleViews.ModuleAPIName
                        );
                      }}
                      data={fv.ModuleViews[0].Views}
                      loading={viewsLoading}
                      labelKey={"PageViewName"}
                      valueKey={"_id"}
                      style={{ width: "100%" }}
                      onClean={() =>
                        onChange(
                          null,
                          fv.Type,
                          fv.APIName,
                          moduleViews.ModuleAPIName
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <ReusableViewWithMultipleSoruces
                fieldSubViewLink={fv}
                viewsLoading={viewsLoading}
                isGlobalTenant={isGlobalTenant}
                setPickerOpened={setPickerOpened}
                reusableViewRefereceField={reusableViews?.ReferenceFields}
                onChange={onChange}
                appName={appName}
                hasFetched={isEnabled && !loadingViews}
              />
            );
          }
        })}
      <div className="small mt-3 mb-4">
        If no options are available, it's because there are no other Views that
        use the same data source.
      </div>
    </>
  );
}
